/* global axios */
// NOTICE: KAPTION-INTEGRATION
// Asistentes (agent providers): which assistant answers each inbox, the shared
// settings, and the Salesforce Agentforce connection. Contract:
// docs/kaption/assistants-api-contract.md
import ApiClient from './ApiClient';

// Writes that deploy to Salesforce (agent behavior, tools, sets, tests,
// restores) take 20–60 s. They get an explicit long timeout so no client
// default can ever cut them while Salesforce is still deploying.
export const SALESFORCE_WRITE_TIMEOUT_MS = 180000;
const LONG_WRITE = { timeout: SALESFORCE_WRITE_TIMEOUT_MS };

class KaptionAssistantsAPI extends ApiClient {
  constructor() {
    super('kaption_assistants', { accountScoped: true });
  }

  // Resumen: { providers: [...], inboxes: [...] }
  getOverview() {
    return axios.get(this.url);
  }

  // { provider: 'studio_chat' | 'agentforce' | 'none', mode?: 'live' | 'test' }
  updateInbox(inboxId, { provider, mode } = {}) {
    const body = { provider };
    if (mode) body.mode = mode;
    return axios.patch(`${this.url}/inboxes/${inboxId}`, body);
  }

  getSettings() {
    return axios.get(`${this.url}/settings`);
  }

  updateSettings(settings) {
    return axios.patch(`${this.url}/settings`, { settings });
  }

  getAgentforce() {
    return axios.get(`${this.url}/agentforce`);
  }

  // client_secret is write-only: leave it out to keep the stored one.
  updateAgentforce(payload) {
    return axios.put(`${this.url}/agentforce`, payload);
  }

  // Empty body tests the saved settings; unsaved fields override them.
  testAgentforce(payload = {}) {
    return axios.post(`${this.url}/agentforce/test`, payload);
  }

  getAgentforceQueues() {
    return axios.get(`${this.url}/agentforce/queues`);
  }

  getAgentforceBehavior() {
    return axios.get(`${this.url}/agentforce/behavior`);
  }

  // tz: the viewer's IANA zone, so "Hoy / 7 días / 30 días" start at their
  // midnight.
  getAgentforceActivity({ range = 'today', filter = 'all', tz } = {}) {
    const params = { range, filter };
    if (tz) params.tz = tz;
    return axios.get(`${this.url}/agentforce/activity`, { params });
  }

  // ── Agentforce management (KAPTION_AGENTFORCE_MANAGEMENT) ───────────────
  // Contract: agentforce-firstclass §1–§6. Writes need management granted
  // (403 { error: 'management_not_granted', missing }).

  // { enabled, status: 'unknown' | 'granted' | 'missing', missing, checked_at }
  getAgentforceManagement() {
    return axios.get(`${this.url}/agentforce/management`);
  }

  // Enabling runs the permission check.
  updateAgentforceManagement(enabled) {
    return axios.put(`${this.url}/agentforce/management`, {
      enabled: !!enabled,
    });
  }

  checkAgentforceManagement() {
    return axios.post(`${this.url}/agentforce/management/check`);
  }

  // { agent, subagents, available_actions, revision }. The server caches
  // the parsed agent for a few minutes; `refresh` reads it from Salesforce.
  getAgentforceAgent({ refresh = false } = {}) {
    return axios.get(`${this.url}/agentforce/agent`, {
      params: refresh ? { refresh: 1 } : {},
    });
  }

  updateAgentforceAgent({ description, revision }) {
    const body = { revision };
    if (description !== undefined) body.description = description;
    return axios.patch(`${this.url}/agentforce/agent`, body, LONG_WRITE);
  }

  upsertAgentforceSubagent(name, payload) {
    return axios.put(
      `${this.url}/agentforce/agent/subagents/${encodeURIComponent(name)}`,
      payload,
      LONG_WRITE
    );
  }

  deleteAgentforceSubagent(name, revision) {
    return axios.delete(
      `${this.url}/agentforce/agent/subagents/${encodeURIComponent(name)}`,
      { params: { revision }, ...LONG_WRITE }
    );
  }

  // [{ id, created_at, actor, summary }], newest first.
  getAgentforceVersions() {
    return axios.get(`${this.url}/agentforce/versions`);
  }

  restoreAgentforceVersion(id) {
    return axios.post(
      `${this.url}/agentforce/versions/${id}/restore`,
      {},
      LONG_WRITE
    );
  }

  // { id, created_at, summary, actor, agent: { description }, subagents }
  getAgentforceVersion(id) {
    return axios.get(`${this.url}/agentforce/versions/${id}`);
  }

  // [tool]; the secret is never returned (has_secret: true).
  getAgentforceTools() {
    return axios.get(`${this.url}/agentforce/tools`);
  }

  // Saves and deploys. Leave auth_secret out to keep the stored one.
  upsertAgentforceTool(key, payload) {
    return axios.put(
      `${this.url}/agentforce/tools/${encodeURIComponent(key)}`,
      payload,
      LONG_WRITE
    );
  }

  // -> { status, latency_ms, body_preview }
  testAgentforceTool(key, { operation, params = {} }) {
    return axios.post(
      `${this.url}/agentforce/tools/${encodeURIComponent(key)}/test`,
      { operation, params }
    );
  }

  // OpenAPI 3 (JSON or YAML text) -> { title, description, base_url, operations }
  parseAgentforceOpenapi(document) {
    return axios.post(`${this.url}/agentforce/tools/parse_openapi`, {
      document,
    });
  }

  deleteAgentforceTool(key) {
    return axios.delete(
      `${this.url}/agentforce/tools/${encodeURIComponent(key)}`,
      LONG_WRITE
    );
  }

  // "Mostrar set de Rumo" action in the org.
  getAgentforceSetsStatus() {
    return axios.get(`${this.url}/agentforce/sets/status`);
  }

  installAgentforceSets(subagents = []) {
    return axios.post(
      `${this.url}/agentforce/sets/install`,
      { subagents },
      LONG_WRITE
    );
  }

  // [{ id, url, active, managed_by_rumo }]
  getAgentforceTrustedDomains() {
    return axios.get(`${this.url}/agentforce/trusted_domains`);
  }

  addAgentforceTrustedDomain(url) {
    return axios.post(`${this.url}/agentforce/trusted_domains`, { url });
  }

  deleteAgentforceTrustedDomain(id) {
    return axios.delete(
      `${this.url}/agentforce/trusted_domains/${encodeURIComponent(id)}`
    );
  }

  // [{ name, label, case_count, last_run }]
  getAgentforceTests() {
    return axios.get(`${this.url}/agentforce/tests`);
  }

  // -> { name, label, cases }
  getAgentforceTest(name) {
    return axios.get(
      `${this.url}/agentforce/tests/${encodeURIComponent(name)}`
    );
  }

  // context_variables ([{ name, value }]) is sent when given; older backends
  // ignore it.
  upsertAgentforceTest(name, { label, cases, context_variables: vars }) {
    const body = { label, cases };
    if (vars !== undefined) body.context_variables = vars;
    return axios.put(
      `${this.url}/agentforce/tests/${encodeURIComponent(name)}`,
      body,
      LONG_WRITE
    );
  }

  deleteAgentforceTest(name) {
    return axios.delete(
      `${this.url}/agentforce/tests/${encodeURIComponent(name)}`,
      LONG_WRITE
    );
  }

  // -> { run_id }
  runAgentforceTest(name) {
    return axios.post(
      `${this.url}/agentforce/tests/${encodeURIComponent(name)}/run`
    );
  }

  // -> { status, started_at, finished_at, cases }
  getAgentforceTestRun(runId) {
    return axios.get(
      `${this.url}/agentforce/test_runs/${encodeURIComponent(runId)}`
    );
  }

  // ── History of sets, tools and tests ───────────────────────────────────
  // [{ id, item_type, key, version, event, summary, changed_fields, snapshot,
  //    actor, created_at }], newest first. itemType: 'set' | 'tool' | 'test'.
  getRevisions(itemType, key) {
    return axios.get(`${this.url}/revisions`, {
      params: { item_type: itemType, key },
    });
  }

  // -> { restored, revision }
  restoreRevision(id) {
    return axios.post(`${this.url}/revisions/${id}/restore`, {}, LONG_WRITE);
  }

  // Any agent of the account; empty list when the flag is off (never 403).
  getLabels() {
    return axios.get(`${this.url}/labels`);
  }
}

export default new KaptionAssistantsAPI();
